'use client';

import { useState } from 'react';
import { cn } from '@skin-seoul/design-system';

/** Just past zero: seeking to exactly 0 is the position the element already holds, so nothing decodes. */
const POSTER_FRAME_SECONDS = 0.001;

interface ReviewVideoPosterProps {
  url: string;
  className?: string;
  onClick?: (event: React.MouseEvent) => void;
}

/**
 * The clip's first frame.
 *
 * A `<video>` paints nothing until a frame is decoded and `preload="metadata"` decodes none, so the
 * seek is issued from script: iOS ignores the `#t=` fragment that asks for the same thing, and left
 * to itself renders the element as a blank tile. The backing colour is what a clip this browser
 * cannot decode falls back to, so a failure reads as a clip rather than as an empty box.
 */
export const ReviewVideoPoster = ({
  url,
  className = 'w-full h-full object-cover',
  onClick,
}: ReviewVideoPosterProps) => {
  const [isUndecodable, setIsUndecodable] = useState(false);

  // Whatever sits on top of the poster — a play mark, a remove button — stays the way in.
  if (isUndecodable) {
    return <div className={cn('bg-neutral-100', className)} />;
  }

  return (
    <video
      src={url}
      className={cn('bg-neutral-100', className)}
      muted
      playsInline
      preload="metadata"
      onLoadedMetadata={(event) => {
        event.currentTarget.currentTime = POSTER_FRAME_SECONDS;
      }}
      onError={() => setIsUndecodable(true)}
      onClick={onClick}
    />
  );
};

export default ReviewVideoPoster;
